import clsx from "clsx";
import { CSSProperties, ReactNode } from "react";

import { Button } from "@/components/atoms/buttons/Button";
import { CtaButtonTypeTemplate } from "@/states/page-editor/template-parts/cta-button";
import { getCorrectUrl } from "@/utils/common";
import { contentToHtml } from "@/utils/draft";

import ArrowIcon from "/public/img/arrow.svg";

interface Props {
  part: CtaButtonTypeTemplate;
  formPath: string | undefined;
  onClick?: () => void;
  disabled?: boolean;
}

// part_lp 9
export default function CtaButton3Content({
  part,
  formPath,
  onClick,
  disabled,
}: Props) {
  const buttonHref = (): string => {
    if (part.buttonContent.link?.url) {
      return getCorrectUrl(part.buttonContent.link?.url);
    }
    if (formPath) {
      return formPath;
    }
    return "";
  };

  const isNewTab = !!(
    part.buttonContent.link?.isNewTab || part.buttonContent.service?.isNewTab
  );

  function ButtonRenderer({
    children,
    styles,
    disabled,
  }: {
    children: ReactNode;
    styles?: CSSProperties;
    disabled?: boolean;
  }) {
    const baseClassNames =
      "relative block text-white font-bold cursor-pointer disabled:pointer-events-none disabled:cursor-default";
    const largeDoubleArrow = [
      baseClassNames,
      "box-border !py-[10px] !pr-[64px] !pl-[16px] !mx-auto !w-full min-h-[64px] rounded-[36px] border-2 border-solid disabled:opacity-30 transition md:min-h-[72px] h-auto",
    ].join(" ");
    const linkRef = buttonHref();

    if (disabled) {
      return (
        <div className={largeDoubleArrow + " opacity-50"} style={styles}>
          {children}
        </div>
      );
    }

    return (
      <Button
        className={largeDoubleArrow}
        style={styles}
        href={linkRef}
        onClick={onClick}
        label={children}
        target={isNewTab ? "_blank" : undefined}
      ></Button>
    );
  }

  return (
    <div
      className="flex relative flex-col gap-6 py-16 px-8 rounded-[16px] md:px-16"
      style={part.frameCssProperties}
    >
      <div className="absolute top-0 left-0 w-full h-full">
        <div
          className="absolute w-full h-full rounded-[16px] border-[3px] border-solid"
          style={part.decorationCssProperties}
        ></div>
        <div className="absolute p-4 w-full h-full">
          <div
            className="w-full h-full rounded-[8px] border-[1px] border-solid"
            style={part.decorationCssProperties}
          ></div>
        </div>
      </div>

      {!!part.description?.content && (
        <div className="m-auto w-full">
          <div
            dangerouslySetInnerHTML={{
              __html: contentToHtml(part.description.content),
            }}
            className={clsx(
              "flex flex-col items-center min-h-[1.5em] text-center break-all",
              "text-2xl font-bold leading-normal md:text-[32px]",
            )}
            style={part.description.textBaseStyle}
          ></div>
        </div>
      )}

      {!!part.buttonContent?.content && (
        <div className="m-auto">
          <ButtonRenderer
            styles={part.buttonContent.cssProperties}
            disabled={disabled}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: contentToHtml(part.buttonContent.content),
              }}
              className="flex flex-col pl-[48px] text-xl font-bold leading-normal text-center md:text-2xl"
              style={part.buttonContent.textBaseStyle}
            />
            <RoundedArrowIcon
              color={part.buttonContent.cssProperties.backgroundColor}
            />
          </ButtonRenderer>
        </div>
      )}
    </div>
  );
}

function RoundedArrowIcon({ color }: { color?: string }) {
  return (
    <div className="flex absolute top-1/2 right-[16px] justify-center items-center w-[32px] h-[32px] bg-white rounded-full -translate-y-1/2">
      <span className="whitespace-nowrap">
        <ArrowIcon width={10} height={16} fill={color} />
      </span>
    </div>
  );
}
